import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Download, CheckCircle2, ShieldAlert, TrendingDown, Target } from "lucide-react";
import { api, API } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { AdSlot } from "@/components/AdSlot";

const SYSTEM_IMG = "https://images.unsplash.com/photo-1544380904-c686aad2fc40?w=1600&q=80";

export default function System() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useSeo({
    title: "The New Baccarat System — Free Briefing | Licence to Play",
    description:
      "The New Baccarat System explained: back the Player every hand with a disciplined staking plan. Get the free PDF briefing — honest maths included.",
    keywords: "baccarat system, new baccarat system, baccarat staking plan, player bet system",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Enter a valid email address, agent.");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/leads", { email, source: "system-page" });
      setEmailSent(!!r.data?.email_sent);
      setSubmitted(true);
      toast.success(r.data?.email_sent ? "Briefing dispatched — check your inbox." : "You're on the list. Grab your PDF below.");
    } catch {
      toast.error("Transmission failed. Try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="relative border-b border-white/10 grain-overlay overflow-hidden">
        <div className="absolute inset-0">
          <img src={SYSTEM_IMG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/90 to-background" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-20">
          <p className="overline text-gold mb-5">▸ Operation: The System</p>
          <h1 className="font-display text-4xl sm:text-6xl text-white tracking-tighter leading-[1.03] max-w-3xl">
            The New Baccarat System
          </h1>
          <p className="mt-6 text-lg text-white/65 max-w-2xl leading-relaxed">
            One rule, one discipline: back the <span className="text-player font-semibold">Player</span> every
            single hand and manage your stakes with a fixed progression plan. Here is exactly how it works —
            and where the danger lies.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14">
        <div className="grid sm:grid-cols-3 gap-5 mb-14">
          {[
            { icon: <Target className="w-5 h-5" />, t: "1. The Bet", d: "Place your unit on Player every hand. No switching, no hunches — consistency is the whole point." },
            { icon: <TrendingDown className="w-5 h-5" />, t: "2. The Progression", d: "After a loss, increase your stake per the plan. After a win, reset to your base unit immediately." },
            { icon: <ShieldAlert className="w-5 h-5" />, t: "3. The Limits", d: "Set a hard stop-loss and a stop-win. When either is hit, the session ends. No exceptions." },
          ].map((s) => (
            <div key={s.t} className="border border-white/10 bg-card p-6">
              <div className="w-10 h-10 grid place-items-center border border-gold/30 text-gold mb-4">{s.icon}</div>
              <h3 className="font-display text-lg text-white">{s.t}</h3>
              <p className="mt-2 text-sm text-white/55 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>

        <div className="prose-briefing">
          <h2>Why the Player bet?</h2>
          <p>
            The Player bet pays even money and carries a house edge of roughly <strong>1.24%</strong> — slightly
            higher than Banker's 1.06%, but it dodges the 5% commission, which keeps the bookkeeping clean and
            the progression easy to follow. For a system built on discipline, simplicity beats a fractional edge.
            If you want the full numbers, read our{" "}
            <Link to="/dossier/banker-vs-player-bet">Banker vs Player breakdown</Link>.
          </p>
          <h2>The staking plan</h2>
          <p>
            The System uses a <strong>negative progression</strong>: your stake rises after each loss and snaps
            back to the base unit on the first win. It is designed to recover a losing run in a single win — but
            that recovery power cuts both ways, and a long cold streak escalates your exposure fast. Model your
            exact numbers in the{" "}
            <Link to="/q-branch">Q Branch staking calculator</Link> before you ever sit at a table, and read the
            deeper theory in{" "}
            <Link to="/dossier/negative-progression-systems">Negative Progression Systems Explained</Link>.
          </p>
          <h2>The honest truth</h2>
          <p>
            No staking plan changes the house edge. Over enough hands, the maths favours the casino regardless of
            how you size your bets. The System's real value is <strong>structure</strong>: it removes emotional
            decisions, enforces limits, and turns a session into a disciplined operation rather than a gamble on
            gut feeling. Treat it as entertainment with guardrails — not an income.
          </p>
        </div>

        <div className="my-12">
          <AdSlot size="728x90" label="In-Content" />
        </div>

        {/* Email capture */}
        <div id="download" className="border border-gold/30 bg-[#0a0c0c] p-8 sm:p-10 relative grain-overlay">
          <div className="relative z-10">
            {!submitted ? (
              <>
                <p className="overline text-gold mb-3">▸ Free PDF Briefing</p>
                <h2 className="font-display text-2xl sm:text-3xl text-white tracking-tight">
                  Get the full System dossier
                </h2>
                <p className="mt-3 text-white/60 leading-relaxed max-w-xl">
                  Enter your email and we'll send you the complete New Baccarat System briefing — staking tables,
                  session checklist and risk notes — as a downloadable PDF.
                </p>
                <form onSubmit={submit} className="mt-7 flex flex-col sm:flex-row gap-3 max-w-xl" data-testid="lead-form">
                  <input
                    data-testid="lead-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="agent@licence.play"
                    className="flex-1 bg-black/50 border border-white/20 text-white px-4 py-3.5 focus:border-gold focus:ring-1 focus:ring-gold/50 outline-none transition-colors"
                  />
                  <button
                    data-testid="lead-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-gold text-black font-semibold text-sm border border-gold hover:bg-transparent hover:text-gold transition-colors disabled:opacity-60"
                  >
                    <Download className="w-4 h-4" /> {loading ? "Sending…" : "Send Briefing"}
                  </button>
                </form>
                <p className="mt-4 overline text-white/30">No spam. Unsubscribe anytime. 18+ only.</p>
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="lead-success"
                className="text-center py-6"
              >
                <CheckCircle2 className="w-12 h-12 text-emerald-bright mx-auto" />
                <h2 className="mt-5 font-display text-2xl text-white">Briefing dispatched</h2>
                <p className="mt-3 text-white/60 max-w-md mx-auto leading-relaxed">
                  {emailSent
                    ? "Check your inbox for the New Baccarat System PDF. If it doesn't arrive within a few minutes, check your spam folder — even secret documents get filtered sometimes."
                    : "Your download is ready. Tap below to open the full New Baccarat System PDF briefing."}
                </p>
                <a
                  href={`${API}/system/briefing.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="briefing-download-link"
                  className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 bg-gold text-black font-semibold text-sm border border-gold hover:bg-transparent hover:text-gold transition-colors"
                >
                  <Download className="w-4 h-4" /> Download the PDF
                </a>
                <div className="mt-5">
                  <Link to="/q-branch" className="inline-flex items-center gap-2 text-gold hover:underline">
                    Try the staking calculator →
                  </Link>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        <div className="mt-8 flex items-start gap-3 text-white/50 text-sm">
          <ShieldAlert className="w-5 h-5 text-crimson-bright shrink-0" />
          <p>
            The System is provided for education and entertainment. It does not guarantee profit and cannot
            overcome the house edge. Please gamble responsibly — see our{" "}
            <Link to="/responsible-gambling" className="text-gold hover:underline">responsible gambling hub</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
